// import node module libraries
import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Row, Col, Button, Dropdown, Badge, Table, Offcanvas, Form } from 'react-bootstrap';
import { MoreVertical, Edit, Trash } from 'react-feather';
import Moment from 'react-moment';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import required custom components
import TanstackTable from 'components/elements/advance-table/TanstackTable';
// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Import the new components
import OffCanvasUpdateTAC from './OffcanvasUpdateTAC';
import ModalDeleteTAC from './ModalDeleteTAC';

const TACSettingsTable = ({ activeKey, TACdata, isLoadingTable, filterBy = '' }) => {
	// Define required states
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [selectedRow, setSelectedRow] = useState({});
	const [isLoadingButtonUpdateTAC, setIsLoadingButtonUpdateTAC] = useState(null); // State for loading spinner

	// Methods / Functions
	const handleRowClick = (row, action) => {
		if (action === 'open') {
			setShowUpdateTACOffcanvas(true);
			fetchRowDetails(row); // Call API to fetch log details
		} else if (action === 'delete') {
			handleDeleteClick(row);
			setShowUpdateTACOffcanvas(false);
		}
	};

	// Delete TAC
	const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing modal
	const [rowToDelete, setRowToDelete] = useState(null); // State for API Credential to delete
	const [isLoadingButtonDeleteTAC, setIsLoadingButtonDeleteTAC] = useState(null); // State for loading spinner

	const handleDeleteClick = (TAC) => {
		setRowToDelete(TAC);
		setShowDeleteModal(true);
	};

	// fetch data from api
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	const handleConfirmDelete = async () => {
		if (rowToDelete) {
			setIsLoadingButtonDeleteTAC(true);
			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			await axios.delete(`${baseURL}/public/terms-and-conditions/${rowToDelete}`, options)
				.then(response => {
					// Update the TACdata state with the deleted row
					const updatedData = TACdata.data.filter((row) => row.id !== rowToDelete);
					TACdata.data = updatedData;
					// Update the TACdata state with the deleted data
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteTAC(false);
					handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
				})
				.catch((error) => {
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteTAC(false);
					const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
					handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				});
		}
	};

	const fetchRowDetails = async (rowId) => {
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		try {
			const response = await axios.get(`${baseURL}/public/terms-and-conditions/${rowId}`, options);
			const rowDetails = response.data;
			setSelectedRow(rowDetails.data); // Update selectedRow state with fetched details

		} catch (error) {
			console.error(error);
		}
	};

	// Update TAC
	const [showUpdateTACOffcanvas, setShowUpdateTACOffcanvas] = useState(false);
	const handleShowUpdateTAC = () => setShowUpdateTACOffcanvas(true);
	const handleCloseUpdateTAC = () => {
		setShowUpdateTACOffcanvas(false);
		setSelectedRow({});
	};

	const handleUpdateTAC = async (e) => {
		e.preventDefault();
		setIsLoadingButtonUpdateTAC(true);

		const data = {
			agb_version: e.target.version.value,
			agb_date: e.target.date.value,
			agb_text: e.target.content.value
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/public/terms-and-conditions/${e.target.id.value}`, data, options)
			.then(response => {
				// Update the selected TAC in the data state
				const updatedRow = response.data;
				setSelectedRow(updatedRow);
				// Update the TACdata state with the updated row
				const updatedData = TACdata.data.map((row) => (row.id === updatedRow.result.id ? 
					{
						id: updatedRow.result.id,
						agb_version: updatedRow.result.agb_version,
						agb_date: updatedRow.result.agb_date,
						agb_text: updatedRow.result.agb_text,
					}
				: row));
				TACdata.data = updatedData;
				// Update the TACdata state with the updated data
				// Close the offcanvas
				setShowUpdateTACOffcanvas(false);
				setIsLoadingButtonUpdateTAC(false);
				// Reset the form fields
				e.target.reset();
				// Show success notification
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				//setShowUpdateTACOffcanvas(false);
				setIsLoadingButtonUpdateTAC(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};
	
	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to="#"
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	// 
	// Components
	//

	// Component ActionMenu
	const ActionMenu = (row) => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle}>
					<MoreVertical size="15px" className="text-secondary" />
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('ACTION')}</Dropdown.Header>
					<Dropdown.Item eventKey="1" onClick={() => handleRowClick(row.ID, 'open')}>
						<Edit size="14px" className="me-1" /> {t('Edit')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="2" onClick={() => handleRowClick(row.ID, 'delete')}>
						<Trash size="14px" className="me-1" /> {t('Delete')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};


	const columns = useMemo(
		() => [
			{
				accessorKey: 'agb_version',
				header: t('Version'),
				cell: ({ row }) => {
					return (
						<Link className="fw-semibold" onClick={(event) => {
							event.preventDefault();
							handleRowClick(row.original.id, 'open');
						}}>
							{row.original.agb_version.toString()} <i className="fe fe-arrow-right fs-4"></i>
						</Link>
					);
				}
			},
			{
				accessorKey: 'agb_date',
				header: t('Created at'),
				cell: ({ getValue }) => {
					return <Moment utc={true} local={true} format="DD.MM.YYYY">{getValue()}</Moment>;
				}
			},
			{
				accessorKey: 'action',
				header: t('Action'),
				cell: ({ row }) => {
					return <ActionMenu ID={row.original.id} />;
				}
			}
		],
		[]
	);

	const unfilteredData = useMemo(() => TACdata?.data ?? [], [TACdata.data]);
	//const pagination = useMemo(() => response?.pagination ?? [], [response.pagination]);

	// filtering....
	const data = useMemo(() => {
		return filterBy === '' 
			? unfilteredData 
			: unfilteredData?.filter((item) => item.agb_version?.includes(filterBy));
	}, [filterBy, unfilteredData]);
	

	if (isLoadingTable) {
		return (
			<Table className="text-nowrap">
				<thead >
					<tr>
						<th scope="col">{t('Title')}</th>
						<th scope="col">{t('Categories')}</th>
						<th scope="col">{t('Action')}</th>
					</tr>
				</thead>
				<tbody>
					{Array(10).fill(0).map((_, index) => (
						<tr key={index}>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
							<td><Skeleton /></td>
						</tr>
					))}
				</tbody>
			</Table>

		);
	}

	return (
		<div>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<TanstackTable
				data={data}
				columns={columns}
				filter={true}
				filterPlaceholder="Suche"
				pagination={true}
				exportButton={false}

			/>
			{/* Update TAC Offcanvas */}
			{selectedRow && Object.keys(selectedRow).length > 0 && (
				<OffCanvasUpdateTAC
					show={showUpdateTACOffcanvas}
					onHide={handleCloseUpdateTAC}
					selectedRow={selectedRow}
					onSubmit={handleUpdateTAC}
					isLoadingButton={isLoadingButtonUpdateTAC}
				/>
			)}
			{/* Delete Log Modal */}
			<ModalDeleteTAC
				show={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				handleConfirmDelete={handleConfirmDelete}
				isLoadingButton={isLoadingButtonDeleteTAC}
			/>
		</div>
	);
};

export default TACSettingsTable;
